import React, { useState, useEffect } from "react";
import { apiCall } from "../../Apis/apiCall";
import { useLocation, useNavigate } from "react-router-dom";
import CustomSelect from "../../components/common/CustomSelect";
import LoadingComponent from "../../components/ui/LoadingComponent";
import Breadcrumb from "../../components/Breadcrumb";
import { handleApiResponse, handleCustomResponse } from "../../utils/response";

// Workflow Preview Modal Component
// Workflow Preview Modal - Exact Image Style
const WorkflowPreviewModal = ({ isOpen, onClose, workflowId, workflowName }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [workflowData, setWorkflowData] = useState(null);

  useEffect(() => {
    if (isOpen && workflowId) {
      fetchWorkflowPreview();
    }
  }, [isOpen, workflowId]);

  const fetchWorkflowPreview = async () => {
    setLoading(true);
    try {
      const url = `/workflow-steps-fields-v2/${workflowId}`;
      const res = await apiCall("get", url, null, navigate);
      const data = res.data.data;
      setWorkflowData(data);
    } catch (error) {
      handleCustomResponse("error", "Failed to fetch workflow preview");
    } finally {
      setLoading(false);
    }
  };

  // Field type icons (matching your WorkFlowEdit)
  const getFieldIcon = (type) => {
    const icons = {
      text: "ri-text",
      textarea: "ri-file-text-line",
      email: "ri-mail-line",
      number: "ri-hashtag",
      tel: "ri-phone-line",
      select: "ri-arrow-down-s-line",
      plate_number: "ri-car-line",
      datetime: "ri-calendar-line",
      image: "ri-image-line",
      color: "ri-palette-line",
      scan_qr: "ri-qr-code-line",
      qr_code: "ri-qr-code-line",
      otp: "ri-key-2-line",
      popup: "ri-external-link-line",
      receipt: "ri-receipt-line",
      ticket_summary: "ri-file-list-3-line",
    };
    return icons[type] || "ri-input-cursor-move";
  };

  if (!isOpen) return null;

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.7)" }}
      onClick={onClose}
    >
      <div
        className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content border-0 shadow-lg overflow-hidden">
          {/* Header - Using theme-aware classes */}
          <div className="modal-header border-bottom-0 pb-0">
            <h6 className="modal-title d-flex align-items-center gap-2 text-primary-600 fw-bold">
              <i className="ri-eye-line"></i>
              Workflow Preview: <span className="text-primary-light">{workflowName}</span>
            </h6>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>

          <div
            className="modal-body p-24"
            style={{ maxHeight: "75vh", overflowY: "auto" }}
          >
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary-600" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-secondary-light">Loading workflow...</p>
              </div>
            ) : workflowData ? (
              <div className="workflow-preview-container position-relative">
                {/* Steps */}
                {workflowData.steps?.map((step, index) => (
                  <div
                    key={step.step_id}
                    className="mb-5 position-relative"
                    style={{ paddingLeft: "50px" }}
                  >
                    {/* Curved Dotted Line */}
                    {/* {index < workflowData.steps.length - 1 && (
                      <svg
                        className="position-absolute start-0"
                        style={{
                          top: "60px",
                          left: "20px",
                          width: "60px",
                          height: "80px",
                          zIndex: 0,
                        }}
                        viewBox="0 0 60 80"
                      >
                        <path
                          d="M 30 0 Q 0 40, 30 80"
                          stroke="var(--neutral-400)"
                          strokeWidth="2"
                          fill="none"
                          strokeDasharray="5,5"
                        />
                      </svg>
                    )} */}

                    {/* Step Card - Using theme-aware classes */}
                    <div
                      className="card border-0 shadow-sm radius-12 overflow-hidden"
                      style={{
                        maxWidth: "420px",
                        margin: "0 auto",
                      }}
                    >
                      {/* Header with gradient */}
                      <div
                        className="p-3 text-base position-relative"
                        style={{
                          background: "linear-gradient(135deg, #fb923c, #f97316)",
                        }}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <h6 className="mb-0 fw-bold text-base">{step.step_label}</h6>
                          <span
                            className="badge bg-base text-neutral-900 fw-bold radius-12"
                            style={{
                              fontSize: "0.75rem",
                              padding: "4px 8px",
                            }}
                          >
                            {index + 1}
                          </span>
                        </div>
                        <small className="d-block mt-1 opacity-90 text-base">
                          Code: {step.step_code}
                        </small>
                      </div>

                      {/* Card Body */}
                      <div className="p-3">
                        {/* Assigned Staff */}
                        {step.assigned_to?.length > 0 && (
                          <div className="mb-3">
                            <small className="text-secondary-light fw-semibold d-block mb-2">
                              <i className="ri-user-line me-1"></i> Assigned Staff:
                            </small>
                            <div className="d-flex flex-wrap gap-1">
                              {step.assigned_to.map((staff, idx) => (
                                <span
                                  key={idx}
                                  className="badge bg-success-50 text-success-600 px-2 py-1 small rounded-pill"
                                >
                                  {staff.staff_name}
                                  {staff.location_name && (
                                    <small className="ms-1 opacity-75">
                                      ({staff.location_name})
                                    </small>
                                  )}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Fields */}
                        {step.fields?.length > 0 ? (
                          <div>
                            <div className="d-flex flex-column gap-2">
                              {step.fields.map((field, fIdx) => (
                                <div
                                  key={field.field_id || fIdx}
                                  className="d-flex align-items-center justify-content-between p-2 radius-8 border bg-neutral-50"
                                  style={{ fontSize: "0.85rem" }}
                                >
                                  <div className="d-flex align-items-center gap-2 flex-grow-1">
                                    <i
                                      className={`${getFieldIcon(
                                        field.type
                                      )} text-primary-600`}
                                    ></i>
                                    <span className="fw-medium text-primary-light text-truncate" style={{ maxWidth: "180px" }}>
                                      {field.label}
                                    </span>
                                    {field.required && (
                                      <span className="text-danger-600 small">*</span>
                                    )}
                                  </div>
                                  <span
                                    className="badge bg-neutral-100 text-neutral-600 small"
                                    style={{ fontSize: "0.65rem" }}
                                  >
                                    {field.type}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <p className="text-secondary-light small mb-0 text-center py-2">
                            <i className="ri-information-line me-1"></i>
                            No fields defined
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* No Steps */}
                {(!workflowData.steps || workflowData.steps.length === 0) && (
                  <div className="text-center py-5">
                    <div className="bg-neutral-50 radius-12 p-4 d-inline-block">
                      <i
                        className="ri-alert-line text-warning-600"
                        style={{ fontSize: "2.5rem" }}
                      ></i>
                      <p className="mt-3 mb-0 text-secondary-light">
                        No steps in this workflow
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="alert alert-danger bg-danger-50 border-danger-600">
                <i className="ri-error-warning-line me-2"></i>
                Failed to load workflow
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="modal-footer border-top-0">
            <button
              type="button"
              className="btn btn-neutral-600 text-base px-4 radius-8"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const WorkFlowAssign = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const stateData = location.state?.stateData || location.state?.locationData;

  // Workflow state
  const [workflows, setWorkflows] = useState([]);
  const [workflowsLoading, setWorkflowsLoading] = useState(false);
  const [workflowSearch, setWorkflowSearch] = useState("");
  const [workflowPage, setWorkflowPage] = useState(1);
  const [workflowTotalPages, setWorkflowTotalPages] = useState(1);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);

  // Branch state
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [branchSearch, setBranchSearch] = useState("");
  const [branchPage, setBranchPage] = useState(1);
  const [branchTotalPages, setBranchTotalPages] = useState(1);
  const [selectedBranch, setSelectedBranch] = useState(null);

  // Location state
  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationSearch, setLocationSearch] = useState("");
  const [locationPage, setLocationPage] = useState(1);
  const [locationTotalPages, setLocationTotalPages] = useState(1);
  const [selectedLocations, setSelectedLocations] = useState([]);

  // Assignment state
  const [assignLoading, setAssignLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  // Preview Modal State
  const [showPreview, setShowPreview] = useState(false);

  // Fetch workflows
  const fetchWorkflows = async (page = 1, search = "") => {
    try {
      setWorkflowsLoading(true);
      const endpoint = `/get-all-workflows?want_pagination=0&sort_by=workflow_id&sort_order=asc`;

      const response = await apiCall("get", endpoint, null, navigate);

      if (response?.data?.success) {
        handleApiResponse(response?.data, "fetch workflows", "GET");

        const workflowData = response.data.data || [];
        const pagination = response.data.pagination || {};

        const mappedWorkflows = workflowData.map((workflow) => ({
          id: workflow.workflow_id,
          name: workflow.name,
          description: workflow.description || "",
        }));

        if (page === 1) {
          setWorkflows(mappedWorkflows);
        } else {
          setWorkflows((prev) => [...prev, ...mappedWorkflows]);
        }

        setWorkflowPage(parseInt(pagination.current_page, 10));
        setWorkflowTotalPages(parseInt(pagination.last_page, 10) || 1);

        if (stateData?.workflowId && page === 1) {
          const prefilledWorkflow = mappedWorkflows.find(
            (w) => w.id === stateData.workflowId
          );
          if (prefilledWorkflow) {
            setSelectedWorkflow(prefilledWorkflow);
          } else {
            setSelectedWorkflow({
              id: stateData.workflowId,
              name: stateData.workflowName,
            });
          }
        }
      } else {
        handleApiResponse(response?.data, "Failed to fetch workflows", "GET");
      }
    } catch (error) {
      const err = error.response?.data || error.message;
      handleApiResponse(err, "Failed to fetch workflows", "GET");
    } finally {
      setWorkflowsLoading(false);
    }
  };

  // Fetch branches
  const fetchBranches = async (page = 1, search = "") => {
    try {
      setBranchesLoading(true);
      const endpoint = `/get-client-branches?want_pagination=0&sort_by=branch_id&sort_order=desc`;

      const response = await apiCall("get", endpoint, null, navigate);

      if (response?.data?.success) {
        handleApiResponse(response?.data, "fetch branches", "GET");

        const branchList = response.data.data || [];
        const pagination = response.data.pagination || {};

        if (page === 1) {
          setBranches(branchList);
        } else {
          setBranches((prev) => [...prev, ...branchList]);
        }

        setBranchPage(parseInt(pagination.current_page || page, 10));
        setBranchTotalPages(parseInt(pagination.last_page, 10) || 1);

        if (stateData?.branchId && page === 1) {
          const prefilledBranch = branchList.find(
            (b) => b.branch_id === stateData.branchId
          );
          if (prefilledBranch) {
            setSelectedBranch(prefilledBranch);
          } else {
            setSelectedBranch({
              branch_id: stateData.branchId,
              branch_name: stateData.branchName,
            });
          }
        }
      } else {
        handleApiResponse(response?.data, "Failed to fetch branches", "GET");
      }
    } catch (error) {
      const err = error.response?.data || error.message;
      handleApiResponse(err, "Failed to fetch branches", "GET");
    } finally {
      setBranchesLoading(false);
    }
  };

  // Fetch locations
  const fetchLocations = async (page = 1, search = "") => {
    try {
      setLocationsLoading(true);
      const branchId = stateData?.branchId || selectedBranch?.branch_id || "";
      const endpoint = `/get-all-branch-locations?want_pagination=0&sort_by=location_id&sort_order=asc&branch_id=${branchId}`;

      const response = await apiCall("get", endpoint, null, navigate);

      if (response?.data?.success) {
        handleApiResponse(response.data, "fetch locations", "GET");

        const locationList = response.data.data || [];
        const pagination = response.data.pagination || {};

        if (page === 1) {
          setLocations(locationList);
        } else {
          setLocations((prev) => [...prev, ...locationList]);
        }

        setLocationPage(parseInt(pagination.current_page || page, 10));
        setLocationTotalPages(parseInt(pagination.last_page, 10) || 1);

        if (stateData?.id && page === 1) {
          const prefilledLocation = locationList.find(
            (l) => l.location_id === stateData.id
          );
          if (prefilledLocation) {
            setSelectedLocations([prefilledLocation.location_id]);
          } else {
            setSelectedLocations([stateData.id]);
          }
        }
      } else {
        handleApiResponse(response.data, "Failed to fetch locations", "GET");
      }
    } catch (error) {
      const err = error.response?.data || error.message;
      handleApiResponse(err, "Failed to fetch locations", "GET");
    } finally {
      setLocationsLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    const loadInitialData = async () => {
      setPageLoading(true);
      await fetchWorkflows();
      await fetchBranches();
      if (stateData?.branchId) {
        await fetchLocations();
      }
      setPageLoading(false);
    };

    loadInitialData();
  }, []);

  // Fetch locations when branch changes
  useEffect(() => {
    if (selectedBranch?.branch_id) {
      fetchLocations(1, "");
    }
  }, [selectedBranch]);

  // Workflow search handler
  const handleWorkflowSearch = (value) => {
    setWorkflowSearch(value);
    setWorkflowPage(1);
    setWorkflows([]);
    fetchWorkflows(1, value);
  };

  // Branch search handler
  const handleBranchSearch = (value) => {
    setBranchSearch(value);
    setBranchPage(1);
    setBranches([]);
    fetchBranches(1, value);
  };

  // Location search handler
  const handleLocationSearch = (value) => {
    setLocationSearch(value);
    setLocationPage(1);
    setLocations([]);
    fetchLocations(1, value);
  };

  // Load more workflows
  const loadMoreWorkflows = () => {
    if (workflowPage < workflowTotalPages && !workflowsLoading) {
      fetchWorkflows(workflowPage + 1, workflowSearch);
    }
  };

  // Load more branches
  const loadMoreBranches = () => {
    if (branchPage < branchTotalPages && !branchesLoading) {
      fetchBranches(branchPage + 1, branchSearch);
    }
  };

  // Load more locations
  const loadMoreLocations = () => {
    if (locationPage < locationTotalPages && !locationsLoading) {
      fetchLocations(locationPage + 1, locationSearch);
    }
  };

  // Workflow select handler
  const handleWorkflowSelect = (selectedOption) => {
    const workflow = workflows.find((w) => w.id === selectedOption?.value);
    setSelectedWorkflow(workflow || null);
  };

  // Branch select handler
  const handleBranchSelect = (selectedOption) => {
    const branch = branches.find((b) => b.branch_id === selectedOption?.value);
    setSelectedBranch(branch || null);
    setSelectedLocations([]);
    setLocations([]);
  };

  // Location select handler
  const handleLocationSelect = (selectedOptions) => {
    const selected = selectedOptions
      ? selectedOptions.map((option) => option.value)
      : [];
    setSelectedLocations(selected);
  };

  // Assign workflow
  const handleAssignWorkflow = async (e) => {
    e.preventDefault();

    if (!selectedWorkflow) {
      handleCustomResponse("error", "Please select a workflow");
      return;
    }

    if (!selectedBranch) {
      handleCustomResponse("error", "Please select a branch");
      return;
    }

    if (selectedLocations.length === 0) {
      handleCustomResponse("error", "Please select at least one location");
      return;
    }

    try {
      setAssignLoading(true);

      const payload = {
        workflow_id: selectedWorkflow.id,
        branch_id: selectedBranch.branch_id,
        location_ids: selectedLocations,
      };

      const response = await apiCall(
        "POST",
        "/assign-workflow-location",
        payload,
        navigate
      );

      if (response?.data?.success) {
        handleApiResponse(
          response.data,
          "Workflow assigned successfully!",
          "POST"
        );

        setSelectedWorkflow(null);
        setSelectedBranch(null);
        setSelectedLocations([]);
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else {
        handleApiResponse(response.data, "Failed to assign workflow", "POST");
      }
    } catch (error) {
      const err = error.response?.data || error.message;
      handleApiResponse(err, "Failed to assign workflow", "POST");
    } finally {
      setAssignLoading(false);
    }
  };

  // Workflow options
  const getWorkflowOptions = () => {
    const options = workflows.map((workflow) => ({
      value: workflow.id,
      label: workflow.name,
    }));
    if (
      stateData?.workflowId &&
      !options.find((opt) => opt.value === stateData.workflowId)
    ) {
      options.push({
        value: stateData.workflowId,
        label: stateData.workflowName,
      });
    }
    return options;
  };

  // Branch options
  const getBranchOptions = () => {
    const options = branches.map((branch) => ({
      value: branch.branch_id,
      label: branch.branch_name,
    }));
    if (
      stateData?.branchId &&
      !options.find((opt) => opt.value === stateData.branchId)
    ) {
      options.push({
        value: stateData.branchId,
        label: stateData.branchName,
      });
    }
    return options;
  };

  // Location options
  const getLocationOptions = () => {
    const options = locations
      .filter(
        (location) =>
          !selectedBranch || location.branch_id === selectedBranch.branch_id
      )
      .map((location) => ({
        value: location.location_id,
        label: location.location_name,
      }));
    if (stateData?.id && !options.find((opt) => opt.value === stateData.id)) {
      options.push({
        value: stateData.id,
        label: stateData.name,
      });
    }
    return options;
  };

  if (pageLoading) {
    return (
      <>
        <Breadcrumb title={"Assign WorkFlow Location"} />
        <LoadingComponent />
      </>
    );
  }

  const workflowOptions = getWorkflowOptions();
  const branchOptions = getBranchOptions();
  const locationOptions = getLocationOptions();

  return (
    <>
      <Breadcrumb title={"Assign WorkFlow Location"} />
      <div className="card h-100 p-0 radius-12">
        <div className="card-body p-24">
          <form onSubmit={handleAssignWorkflow}>
            <div className="row g-20">
              {/* Workflow Selection */}
              <div className="col-md-4 mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Select Workflow <span className="text-danger-600">*</span>
                </label>
                <div className="d-flex align-items-center gap-2">
                  <div className="flex-grow-1">
                    <CustomSelect
                      name="workflow_id"
                      value={selectedWorkflow ? selectedWorkflow.id : null}
                      onChange={handleWorkflowSelect}
                      onInputChange={handleWorkflowSearch}
                      onMenuScrollToBottom={loadMoreWorkflows}
                      options={workflowOptions}
                      placeholder="Search and select workflow..."
                      isLoading={workflowsLoading}
                      isDisabled={assignLoading}
                      addNewLabel="Add New Workflow"
                      addNewRoute="/workflows"
                      isClearable
                    />
                  </div>
                  {selectedWorkflow && (
                    <button
                      type="button"
                      className="btn btn-outline-primary px-3 py-2"
                      onClick={() => setShowPreview(true)}
                      title="Preview Workflow"
                    >
                      <i className="ri-eye-line"></i>
                    </button>
                  )}
                </div>
              </div>

              {/* Branch Selection */}
              <div className="col-md-4 mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Select Branch <span className="text-danger-600">*</span>
                </label>
                <CustomSelect
                  name="branch_id"
                  value={selectedBranch ? selectedBranch.branch_id : null}
                  onChange={handleBranchSelect}
                  onInputChange={handleBranchSearch}
                  onMenuScrollToBottom={loadMoreBranches}
                  options={branchOptions}
                  placeholder="Search and select branch..."
                  isLoading={branchesLoading}
                  isDisabled={assignLoading || !selectedWorkflow}
                  addNewLabel="Add New Branch"
                  addNewRoute="/branch-create"
                  isClearable
                />
              </div>

              {/* Location Selection */}
              <div className="col-md-4 mb-20">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Select Locations <span className="text-danger-600">*</span>
                </label>
                <CustomSelect
                  name="location_ids"
                  isMulti
                  value={selectedLocations}
                  onChange={handleLocationSelect}
                  onInputChange={handleLocationSearch}
                  onMenuScrollToBottom={loadMoreLocations}
                  options={locationOptions}
                  placeholder="Search and select locations..."
                  isLoading={locationsLoading}
                  isDisabled={assignLoading || !selectedBranch}
                  addNewLabel="Add New Location"
                  addNewRoute="/branch-location-create"
                  isClearable
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="d-flex align-items-center justify-content-center gap-3 mt-4">
              <button
                type="submit"
                className="btn btn-primary-600 border border-primary-600 text-md px-56 py-12 radius-8"
                disabled={
                  assignLoading ||
                  !selectedWorkflow ||
                  !selectedBranch ||
                  selectedLocations.length === 0
                }
              >
                {assignLoading ? "Assigning..." : "Assign Workflow"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Workflow Preview Modal */}
      <WorkflowPreviewModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        workflowId={selectedWorkflow?.id}
        workflowName={selectedWorkflow?.name}
      />
    </>
  );
};

export default WorkFlowAssign;